import { useSpaceLimitErrorHandler } from "@circle-react/components/Spaces/hooks/useSpaceLimitErrorHandler";
import { useSpacesContext } from "@circle-react/contexts/spacesContext";
import { isCourseSpace } from "@circle-react/helpers/spaceHelpers";
import {
  isSettingsArea,
  settingsNavPathV3,
} from "@circle-react/helpers/urlHelpers";
import { useSpaceApi } from "@circle-react/hooks/useSpaceApi";
import type { Space } from "@circle-react/types/Space";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { notifyBugsnag } from "@/react/helpers/bugsnagHelpers";

import type { SpaceApiError, SpaceFormValues } from "../types";
import { buildSpaceFormPayload } from "../utils/buildPayload";

interface UseCreateSpaceSubmitArgs {
  onClose: () => void;
  setExternalLoadingState: (state: boolean) => void;
}

interface UseCreateSpaceSubmitResult {
  handleSubmit: (data: SpaceFormValues) => Promise<void>;
}

export const useCreateSpaceSubmit = ({
  onClose,
  setExternalLoadingState,
}: UseCreateSpaceSubmitArgs): UseCreateSpaceSubmitResult => {
  const navigate = useNavigate();
  const toast = useToast();
  const {
    actions: { refresh },
  } = useSpacesContext();
  const { createSpace, maxSpaceLimit } = useSpaceApi();
  const { handleSpaceLimitError, isSpaceLimitModalAvailable } =
    useSpaceLimitErrorHandler({
      onBeforeModal: () => setExternalLoadingState(false),
      onUpgradeSuccess: () => {
        onClose();
        void navigate({ to: settingsNavPathV3.content.spaces });
      },
    });

  const checkPlanRestrictions = useCallback(
    async (spaceGroupId?: number) => {
      if (spaceGroupId === undefined) {
        return;
      }
      if (isSpaceLimitModalAvailable) {
        await maxSpaceLimit(spaceGroupId);
      }
    },
    [isSpaceLimitModalAvailable, maxSpaceLimit],
  );

  const handleSubmit = useCallback(
    async (data: SpaceFormValues) => {
      setExternalLoadingState(true);

      const spaceName = data.name.trim();

      try {
        await checkPlanRestrictions(data.space_group_id);
        const payload = buildSpaceFormPayload(data);
        const newSpace = (await createSpace<Space>(payload)) as Space;
        await refresh();
        toast.success(
          t("spaces.form.create.v4.toast.created", {
            spaceName: spaceName || newSpace.name,
          }),
        );
        onClose();

        if (isCourseSpace(newSpace) && isSettingsArea()) {
          void navigate({
            to: "/settings/courses/$id/edit",
            params: { id: String(newSpace.id) },
          });
        } else {
          void navigate({
            to: "/c/$spaceSlug",
            params: { spaceSlug: newSpace.slug },
          });
        }
      } catch (rawError) {
        const apiError = rawError as SpaceApiError;
        notifyBugsnag(rawError as Error);
        console.error(apiError);

        const wasSpaceLimitError = await handleSpaceLimitError(apiError);
        if (!wasSpaceLimitError) {
          toast.error(t("spaces.form.create.v4.toast.error", { spaceName }));
        }
      } finally {
        setExternalLoadingState(false);
      }
    },
    [
      checkPlanRestrictions,
      createSpace,
      navigate,
      handleSpaceLimitError,
      onClose,
      refresh,
      setExternalLoadingState,
      toast,
    ],
  );

  return { handleSubmit };
};
